import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../shared/components/LanguageSwitcher";

const HERO_IMAGES = {
  fi: "/assets/paranoidi/biisitoiveet-keikalla-demo-fi.webp",
  en: "/assets/paranoidi/biisitoiveet-keikalla-demo-en.webp",
};

const AUDIENCE_STEP_IDS = ["scanQr", "findSong", "manageRequest"];

const REQUEST_FEED_ITEMS = {
  fi: [
    {
      title: "Kuuma kesä",
      metaKey: "birthdayGroupNew",
    },
    {
      title: "Mä joka päivä töitä teen",
      metaKey: "tableSevenNew",
    },
    {
      title: "Beibi",
      metaKey: "lauraSeen",
    },
  ],
  en: [
    {
      title: "Dancing Queen",
      metaKey: "birthdayGroupNew",
    },
    {
      title: "Summer of '69",
      metaKey: "tableSevenNew",
    },
    {
      title: "I Want It That Way",
      metaKey: "lauraSeen",
    },
  ],
};

const PAIN_POINT_IDS = [
  "paperNotes",
  "whatsapp",
  "socialComments",
  "stageShouts",
];

const PERFORMER_BENEFIT_IDS = ["eventTargeting", "sameLibrary"];

const USE_CASE_IDS = [
  "restaurantBars",
  "weddingsPrivate",
  "corporateEvents",
  "liveSocial",
];

const FAQ_IDS = ["audienceApp", "fileImport", "afterTrial"];

function RequestFeedCard({ item, index }) {
  return (
    <div
      className="rounded-xl border border-white/10 bg-black/55 px-3 py-2 shadow-xl shadow-black/20 backdrop-blur sm:rounded-2xl sm:px-4 sm:py-3"
      style={{
        animation: `paranoidiFloatFeed 5s ease-in-out ${
          index * 450
        }ms infinite`,
      }}
    >
      <div className="flex min-w-0 items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-xs font-black uppercase tracking-[-0.02em] text-white sm:text-sm">
            {item.title}
          </p>

          <p className="mt-1 truncate text-[0.68rem] font-bold text-zinc-500 sm:text-xs">
            {item.meta}
          </p>
        </div>

        <span className="h-2 w-2 shrink-0 rounded-full bg-red-400 shadow-[0_0_18px_rgba(248,113,113,0.9)] sm:h-2.5 sm:w-2.5" />
      </div>
    </div>
  );
}

function AudienceStep({ step }) {
  return (
    <article className="group rounded-[2rem] border border-white/10 bg-white/[0.035] p-6 transition duration-300 hover:-translate-y-1 hover:border-red-400/30 hover:bg-white/[0.055]">
      <p className="text-sm font-black uppercase tracking-[0.18em] text-red-400">
        {step.number}
      </p>

      <h3 className="mt-4 text-2xl font-black uppercase leading-none tracking-[-0.05em] text-white">
        {step.title}
      </h3>

      <p className="mt-4 text-sm font-semibold leading-7 text-zinc-400">
        {step.text}
      </p>
    </article>
  );
}

export default function SongRequestsLandingPage() {
  const { t, i18n } = useTranslation("admin");
  const language = i18n.resolvedLanguage?.toLowerCase().startsWith("en")
    ? "en"
    : "fi";
  const heroImage = HERO_IMAGES[language];

  const audienceSteps = AUDIENCE_STEP_IDS.map((id, index) => ({
    id,
    number: String(index + 1).padStart(2, "0"),
    title: t(`songRequestsLandingPage.steps.items.${id}.title`),
    text: t(`songRequestsLandingPage.steps.items.${id}.text`),
  }));

  const requestFeed = REQUEST_FEED_ITEMS[language].map((item) => ({
    ...item,
    meta: t(`songRequestsLandingPage.hero.requestFeed.${item.metaKey}`),
  }));

  const painPoints = PAIN_POINT_IDS.map((id) => ({
    id,
    label: t(`songRequestsLandingPage.why.painPoints.${id}.label`),
    text: t(`songRequestsLandingPage.why.painPoints.${id}.text`),
  }));

  const performerBenefits = PERFORMER_BENEFIT_IDS.map((id) => ({
    id,
    title: t(`songRequestsLandingPage.performer.items.${id}.title`),
    text: t(`songRequestsLandingPage.performer.items.${id}.text`),
  }));

  const useCaseCards = USE_CASE_IDS.map((id) => ({
    id,
    title: t(`songRequestsLandingPage.useCases.items.${id}.title`),
    text: t(`songRequestsLandingPage.useCases.items.${id}.text`),
  }));

  const faqItems = FAQ_IDS.map((id) => ({
    id,
    question: t(`songRequestsLandingPage.faq.items.${id}.question`),
    answer: t(`songRequestsLandingPage.faq.items.${id}.answer`),
  }));

  return (
    <main className="min-h-screen overflow-hidden bg-[#050505] text-white">
      <style>
        {`
          @keyframes paranoidiHeroIn {
            0% {
              opacity: 0;
              transform: translateY(28px) scale(0.98);
            }

            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          @keyframes paranoidiSoftPulse {
            0%, 100% {
              opacity: 0.35;
              transform: scale(1);
            }

            50% {
              opacity: 0.78;
              transform: scale(1.08);
            }
          }

          @keyframes paranoidiFloatFeed {
            0%, 100% {
              transform: translateY(0);
            }

            50% {
              transform: translateY(-8px);
            }
          }

          @keyframes paranoidiScan {
            0% {
              transform: translateY(-120%);
              opacity: 0;
            }

            18% {
              opacity: 0.75;
            }

            100% {
              transform: translateY(220%);
              opacity: 0;
            }
          }


          details[open] .paranoidi-faq-icon {
            transform: rotate(45deg);
            border-color: rgba(248, 113, 113, 0.45);
            background: rgba(239, 68, 68, 0.12);
          }

          .paranoidi-songrequests-language {
            gap: 0 !important;
          }

          .paranoidi-songrequests-language > span:first-child {
            display: none;
          }

          @media (prefers-reduced-motion: reduce) {
            [class*="paranoidiHeroIn"],
            [class*="paranoidiSoftPulse"],
            [class*="paranoidiFloatFeed"],
            [class*="paranoidiScan"] {
              animation: none !important;
            }
          }
        `}
      </style>

      <section className="relative min-h-screen overflow-hidden border-b border-white/10">
        <div className="theme-dark absolute right-4 top-4 z-30 sm:right-6 sm:top-6 lg:right-10 lg:top-8">
          <LanguageSwitcher
            variant="public"
            className="paranoidi-songrequests-language !mt-0"
          />
        </div>

        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(239,68,68,0.34),transparent_30rem),radial-gradient(circle_at_80%_15%,rgba(255,255,255,0.10),transparent_26rem),linear-gradient(135deg,rgba(127,29,29,0.28),transparent_48%)]" />
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:44px_44px]" />
        <div className="pointer-events-none absolute left-1/2 top-10 h-96 w-96 -translate-x-1/2 rounded-full bg-red-500/10 blur-3xl [animation:paranoidiSoftPulse_5s_ease-in-out_infinite]" />

        <div className="relative mx-auto grid min-h-screen max-w-[96rem] gap-8 px-5 pb-10 pt-4 sm:px-8 sm:pb-12 sm:pt-8 lg:grid-cols-[0.84fr_1.16fr] lg:items-center lg:px-10 lg:py-16">
          <div className="max-w-4xl [animation:paranoidiHeroIn_700ms_ease-out_both]">
            <h1 className="mt-20 text-[2.85rem] font-black uppercase leading-[0.84] tracking-[-0.08em] text-white sm:mt-6 sm:text-6xl lg:text-8xl">
              {t("songRequestsLandingPage.hero.title")}
            </h1>

            <p className="mt-7 max-w-3xl text-lg font-semibold leading-8 text-zinc-300 sm:text-xl sm:leading-9">
              {t("songRequestsLandingPage.hero.intro")}
            </p>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <a
                href="/login"
                className="inline-flex items-center justify-center rounded-full bg-red-500 px-7 py-4 text-sm font-black uppercase tracking-[0.14em] text-white shadow-lg shadow-red-950/30 transition hover:-translate-y-0.5 hover:bg-red-400"
              >
                {t("songRequestsLandingPage.actions.startTrial")}
              </a>

              <a
                href="#miten-toimii"
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/[0.04] px-7 py-4 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:-translate-y-0.5 hover:bg-white/[0.08]"
              >
                {t("songRequestsLandingPage.actions.howItWorks")}
              </a>
            </div>
          </div>

          <div className="relative [animation:paranoidiHeroIn_900ms_ease-out_120ms_both]">
            <div className="absolute -inset-6 rounded-[3rem] bg-red-500/10 blur-3xl" />

            <div className="relative overflow-hidden rounded-[2.25rem] border border-white/10 bg-black shadow-2xl shadow-black/50">
              <img
                src={heroImage}
                alt={t("songRequestsLandingPage.hero.imageAlt")}
                className="h-full w-full object-cover"
                loading="eager"
              />

              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_28%_42%,transparent_0,transparent_16rem,rgba(0,0,0,0.18)_27rem),linear-gradient(90deg,rgba(0,0,0,0.08),rgba(0,0,0,0.22))]" />

              <div className="absolute left-6 top-6 hidden max-w-[18rem] rounded-2xl border border-white/10 bg-black/55 px-4 py-3 backdrop-blur sm:block">
                <p className="mt-1 text-2xl font-black text-white">
                  {t("songRequestsLandingPage.hero.overlayTitle")}
                </p>
              </div>

              <div className="absolute bottom-6 right-6 hidden w-[17rem] gap-3 sm:grid">
                {requestFeed.map((item, index) => (
                  <RequestFeedCard
                    key={item.id}
                    item={item}
                    index={index}
                  />
                ))}
              </div>

              <div className="pointer-events-none absolute left-[30%] top-0 hidden h-full w-px bg-gradient-to-b from-transparent via-red-300/70 to-transparent [animation:paranoidiScan_4s_ease-in-out_infinite] sm:block" />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-10 lg:py-20">
        <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.24em] text-red-400">
              {t("songRequestsLandingPage.why.eyebrow")}
            </p>

            <h2 className="mt-4 text-4xl font-black uppercase tracking-[-0.06em] text-white sm:text-5xl">
              {t("songRequestsLandingPage.why.title")}
            </h2>

            <p className="mt-5 text-base font-semibold leading-8 text-zinc-400">
              {t("songRequestsLandingPage.why.text")}
            </p>
          </div>

          <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.035]">
            {painPoints.map((item) => (
              <div
                key={item.id}
                className="grid border-b border-white/10 last:border-b-0 sm:grid-cols-[0.75fr_1fr]"
              >
                <div className="border-white/10 px-5 py-4 text-sm font-black uppercase tracking-[0.08em] text-white sm:border-r">
                  {item.label}
                </div>

                <div className="border-white/10 px-5 py-4 text-sm font-semibold text-zinc-500 sm:border-r">
                  {item.text}
                </div>

              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        id="miten-toimii"
        className="scroll-mt-8 border-y border-white/10 bg-white/[0.025]"
      >
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-10 lg:py-20">
          <div className="max-w-3xl">
            <p className="text-sm font-black uppercase tracking-[0.24em] text-red-400">
              {t("songRequestsLandingPage.steps.eyebrow")}
            </p>

            <h2 className="mt-4 text-4xl font-black uppercase tracking-[-0.06em] text-white sm:text-5xl">
              {t("songRequestsLandingPage.steps.title")}
            </h2>
          </div>

          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {audienceSteps.map((step) => (
              <AudienceStep key={step.id} step={step} />
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-[#070707]">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-10 lg:py-20">
          <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
            <div className="max-w-xl">
              <p className="text-sm font-black uppercase tracking-[0.24em] text-red-400">
                {t("songRequestsLandingPage.performer.eyebrow")}
              </p>

              <h2 className="mt-4 text-4xl font-black uppercase tracking-[-0.06em] text-white sm:text-5xl">
                {t("songRequestsLandingPage.performer.title")}
              </h2>

              <p className="mt-5 text-base font-semibold leading-8 text-zinc-400">
                {t("songRequestsLandingPage.performer.text")}
              </p>

              <a
                href={
                  language === "en"
                    ? "/en/features"
                    : "/ominaisuudet"
                }
                className="mt-7 inline-flex items-center justify-center rounded-full border border-white/15 bg-white/[0.04] px-6 py-3 text-sm font-black uppercase tracking-[0.12em] text-white transition hover:border-red-400/40 hover:bg-white/[0.08]"
              >
                {t("songRequestsLandingPage.actions.allFeatures")}
              </a>

              {language === "en" && (
                <div className="mt-3">
                  <a
                    href="/en/setlist-app-for-musicians"
                    className="inline-flex items-center justify-center rounded-full border border-red-400/30 bg-red-500/10 px-6 py-3 text-sm font-black uppercase tracking-[0.12em] text-red-200 transition hover:-translate-y-0.5 hover:border-red-400/60 hover:bg-red-500/20 hover:text-white"
                  >
                    Explore the setlist app for musicians
                  </a>
                </div>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {performerBenefits.map((item, index) => (
                <article
                  key={item.id}
                  className="rounded-[2rem] border border-white/10 bg-white/[0.035] p-6 transition duration-300 hover:-translate-y-1 hover:border-red-400/30 hover:bg-white/[0.055]"
                >
                  <p className="text-sm font-black uppercase tracking-[0.18em] text-red-400">
                    {String(index + 1).padStart(2, "0")}
                  </p>

                  <h3 className="mt-4 text-2xl font-black uppercase leading-none tracking-[-0.05em] text-white">
                    {item.title}
                  </h3>

                  <p className="mt-4 text-sm font-semibold leading-7 text-zinc-400">
                    {item.text}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden border-b border-white/10 bg-black py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-black uppercase tracking-[0.24em] text-red-400">
              {t("songRequestsLandingPage.useCases.eyebrow")}
            </p>

            <h2 className="mt-4 text-4xl font-black uppercase tracking-[-0.06em] text-white sm:text-5xl">
              {t("songRequestsLandingPage.useCases.title")}
            </h2>

            <p className="mt-5 text-base font-semibold leading-8 text-zinc-400">
              {t("songRequestsLandingPage.useCases.text")}
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {useCaseCards.map((item) => (
              <article
                key={item.id}
                className="rounded-[2rem] border border-white/10 bg-white/[0.035] p-6 transition duration-300 hover:border-red-400/30 hover:bg-white/[0.055]"
              >
                <h3 className="text-2xl font-black uppercase tracking-[-0.05em] text-white">
                  {item.title}
                </h3>

                <p className="mt-4 text-sm font-semibold leading-7 text-zinc-400">
                  {item.text}
                </p>
              </article>
            ))}
          </div>
        </div>

      </section>

      <section
        id="ukk"
        className="border-b border-white/10 bg-white/[0.025]"
      >
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[0.8fr_1.2fr] lg:px-10 lg:py-20">
          <div className="max-w-xl">
            <p className="text-sm font-black uppercase tracking-[0.24em] text-red-400">
              {t("songRequestsLandingPage.faq.eyebrow")}
            </p>

            <h2 className="mt-4 text-4xl font-black uppercase tracking-[-0.06em] text-white sm:text-5xl">
              {t("songRequestsLandingPage.faq.title")}
            </h2>

          </div>

          <div className="grid gap-3">
            {faqItems.map((item) => (
              <details
                key={item.id}
                className="group rounded-[1.5rem] border border-white/10 bg-black/40 px-5 py-4 open:border-red-400/30 open:bg-white/[0.045]"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-5 text-base font-black text-white">
                  <span>{item.question}</span>

                  <span
                    aria-hidden="true"
                    className="paranoidi-faq-icon flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 text-red-300 transition duration-200"
                  >
                    +
                  </span>
                </summary>

                <p className="mt-4 max-w-3xl border-t border-white/10 pt-4 text-sm font-semibold leading-7 text-zinc-400">
                  {item.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#090303]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(239,68,68,0.20),transparent_34rem)]" />

        <div className="relative mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:px-10 lg:py-20">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.24em] text-red-300">
              Paranoidi
            </p>

            <h2 className="mt-4 text-4xl font-black uppercase tracking-[-0.06em] text-white sm:text-5xl">
              {t("songRequestsLandingPage.cta.title")}
            </h2>

          </div>

          <div className="rounded-[2rem] border border-white/10 bg-black/45 p-6 shadow-2xl shadow-black/30">
            <p className="text-2xl font-black uppercase tracking-[-0.05em] text-white">
              {t("songRequestsLandingPage.cta.text")}
            </p>


            <div className="mt-6 grid gap-3">
              <a
                href="/login"
                className="inline-flex items-center justify-center rounded-full bg-red-500 px-6 py-4 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:bg-red-400"
              >
                {t("songRequestsLandingPage.actions.startTrial")}
              </a>

              <a
                href={
                  language === "en"
                    ? "/en/features"
                    : "/ominaisuudet"
                }
                className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/[0.04] px-6 py-4 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:bg-white/[0.08]"
              >
                {t("songRequestsLandingPage.actions.compareFeatures")}
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
